import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Checkbox, IconButton, Menu, MenuItem,
  Dialog, Slide, Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { fetchData, deleteData, putData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import GoogleClassroomForm from "./GoogleClassroomForm";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallbackRows = [
  { id: null, classGrade: "Nilai01", subject: "Tamil Reading", classroomUrl: "https://classroom.google.com/c/nilai01", classroomCode: "abc123", academicYear: "2025-2026", status: "Upcoming" },
  { id: null, classGrade: "Nilai02", subject: "Tamil Writing", classroomUrl: "https://classroom.google.com/c/nilai02", classroomCode: "def456", academicYear: "2025-2026", status: "Completed" },
  { id: null, classGrade: "Nilai03", subject: "Tamil Grammar", classroomUrl: "https://classroom.google.com/c/nilai03", classroomCode: "ghi789", academicYear: "2025-2026", status: "Upcoming" },
  { id: null, classGrade: "Nilai04", subject: "Tamil Speaking", classroomUrl: "https://classroom.google.com/c/nilai04", classroomCode: "jkl012", academicYear: "2025-2026", status: "Upcoming" },
  { id: null, classGrade: "Nilai05", subject: "Homework", classroomUrl: "https://classroom.google.com/c/nilai05", classroomCode: "mno345", academicYear: "2025-2026", status: "Completed" },
];

const statusStyle = (status) => {
  if (status === "Upcoming") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  return { backgroundColor: "#ffebee", color: "#f44336" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

function ActionMenu({ row, onEdit, onDelete, onToggleStatus }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isInactive = row.status?.toLowerCase() === "inactive";

  const handleEdit   = () => { setAnchorEl(null); onEdit(row); };
  const handleDelete = () => { setAnchorEl(null); onDelete(row); };
  const handleToggle = () => { setAnchorEl(null); onToggleStatus(row); };
  const handleView   = () => {
    setAnchorEl(null);
    if (row.classroomUrl || row.url) window.open(row.classroomUrl || row.url, "_blank");
  };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleView}>View</MenuItem>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleToggle} sx={{ color: isInactive ? "#4caf50" : "#ff9800" }}>
          {isInactive ? "Activate" : "Deactivate"}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#f44336" }}>Delete</MenuItem>
      </Menu>
    </>
  );
}

export default function GoogleClassroom() {
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleEdit = useCallback((row) => {
    setEditRow(row);
    setFormOpen(true);
  }, []);

  const handleDeleteRequest = useCallback((row) => {
    setDeleteRow(row);
  }, []);

  const handleToggleStatus = useCallback(async (row) => {
    if (!row?.id) return;
    const newStatus = row.status?.toLowerCase() === "inactive" ? "Active" : "Inactive";
    try {
      await putData(`teacher/classroom/${row.id}/status`, { status: newStatus });
    } catch (_) { /* Optimistic update fallback */ }
    setRawData(prev => prev.map(r =>
      r.id === row.id ? { ...r, status: newStatus } : r
    ));
    setToast({ open: true, severity: "success", message: `Classroom ${newStatus === "Inactive" ? "deactivated" : "activated"} — students will ${newStatus === "Inactive" ? "no longer see" : "now see"} the Open button.` });
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteRow?.id) { setDeleteRow(null); return; }
    try {
      await deleteData(`teacher/classroom/${deleteRow.id}`);
      setToast({ open: true, severity: "success", message: "Classroom deleted" });
      setDeleteRow(null);
      loadData();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteRow(null);
    }
  };

  const buildRows = useCallback((data, onEdit, onDelete) => {
    return data.map((r) => ({
      checkbox: <Checkbox size="small" />,
      classGrade: r.classGrade || r.grade || r.class,
      subject: r.subject,
      url: (
        <a href={r.classroomUrl || r.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", fontSize: "12px", wordBreak: "break-all" }}>
          {r.classroomUrl || r.url}
        </a>
      ),
      code: r.classroomCode || r.code,
      academicYear: r.academicYear,
      statusChip: (
        <Chip label={r.status} size="small" sx={{ ...statusStyle(r.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
      ),
      action: <ActionMenu row={r} onEdit={onEdit} onDelete={onDelete} onToggleStatus={handleToggleStatus} />,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleToggleStatus]);

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("teacher/classroom/list");
      const data = response?.data?.classrooms && Array.isArray(response.data.classrooms)
        ? response.data.classrooms
        : (response?.data && Array.isArray(response.data) ? response.data : fallbackRows);
      setRawData(data);
      setRows(buildRows(data, handleEdit, handleDeleteRequest));
    } catch (_) {
      setRawData(fallbackRows);
      setRows(buildRows(fallbackRows, handleEdit, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRows, handleEdit, handleDeleteRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (rawData.length > 0) setRows(buildRows(rawData, handleEdit, handleDeleteRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  const columns = [
    { label: "", field: "checkbox" },
    { label: "Class", field: "classGrade" },
    { label: "Subject", field: "subject" },
    { label: "Classroom URL", field: "url" },
    { label: "Classroom Code", field: "code" },
    { label: "Academic Year", field: "academicYear" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "action" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Google Classroom
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Google Classroom</h2>
          <Chip label={`${rawData.length} Records`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<AddIcon />} onClick={() => { setEditRow(null); setFormOpen(true); }} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            Create
          </Button>
        </Box>
      </Box>
      <DynamicTable columns={columns} rows={rows} />

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => { setFormOpen(false); setEditRow(null); }} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <GoogleClassroomForm
          initialData={editRow}
          handleClose={() => { setFormOpen(false); setEditRow(null); loadData(); }}
        />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Delete Classroom</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>
            Are you sure you want to delete this classroom? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteRow(null)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d32f2f" } }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
